// src/pages/CreateAd.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  FormControlLabel,
  Switch,
  Paper,
  Chip,
  Tooltip,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { CalendarToday } from "@mui/icons-material";
import config from "../config";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";
const baseUrl = config.baseURL;

const CreateAd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = Boolean(location.state?.adId);
  const [dateError, setDateError] = useState({ start: "", end: "" });

  const [form, setForm] = useState({
    title: "",
    description: "",
    media_file: [],
    // tags: "",
    call_to_action: "link",
    call_to_action_value: "",
    zone_ids: [],
    status: true, // true = active, false = inactive
    targeting_industries: "",
    targeting_categories: "",
    bid_price: "",
    max_budget: "",
    device_target: "all",
    start_date: "",
    end_date: "",
    retargeting_override: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [zones, setZones] = useState([]);
  const [advertiserId, setAdvertiserId] = useState(null);

  const token = localStorage.getItem("token");

  // Decode token
  useEffect(() => {
    if (!token) return;
    try {
      const decoded = jwtDecode(token);
      setAdvertiserId(decoded.advertiserId || null);
    } catch (err) {
      console.error("Invalid token", err);
    }
  }, [token]);

  // Fetch zones
  useEffect(() => {
    const fetchZones = async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/zones`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setZones(res.data || []);
      } catch (err) {
        console.error("Failed to fetch zones", err);
      }
    };
    fetchZones();
  }, [token]);

  // Fetch ad details if editing
  useEffect(() => {
    if (isEditing) {
      const fetchAd = async () => {
        try {
          const res = await axios.get(
            `${baseUrl}/api/ads/${location.state.adId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const ad = res.data;
          setForm({
            title: ad.title || "",
            description: ad.description || "",
            media_file: [],
            // tags: ad.tags ? ad.tags.join(",") : "",
            call_to_action: ad.call_to_action || "link",
            call_to_action_value: ad.call_to_action_value || "",
            zone_ids: ad.zone_ids || [],
            status: ad.status === "active",
            targeting_industries: ad.targeting_industries
              ? ad.targeting_industries.join(",")
              : "",
            targeting_categories: ad.targeting_categories
              ? ad.targeting_categories.join(",")
              : "",
            bid_price: ad.bid_price || "",
            max_budget: ad.max_budget || "",
            device_target: ad.device_target || "all",
            start_date: ad.start_date || "",
            end_date: ad.end_date || "",
            retargeting_override: ad.retargeting_override || false,
          });
        } catch (err) {
          console.error(err);
          setError("Failed to load ad for editing.");
        }
      };
      fetchAd();
    }
  }, [isEditing, location.state, token]);
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (name === "media_file") {
      setForm((prev) => ({
        ...prev,
        media_file: Array.from(files), // ✅ store File objects
      }));
    } else if (type === "checkbox") {
      setForm((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleZoneChange = (event) => {
    const {
      target: { value },
    } = event;
    setForm((prev) => ({
      ...prev,
      zone_ids: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!advertiserId) return;

    if (form.end_date && form.start_date && form.end_date < form.start_date) {
      setError("End date cannot be before start date.");
      return;
    }

    setLoading(true);
    setError("");
    // WhatsApp validation
    if (form.call_to_action === "whatsapp") {
      const number = form.call_to_action_value.replace(/\D/g, ""); // remove non-digit chars
      if (!number) {
        setError("Please enter a valid WhatsApp number.");
        return;
      }
      // Convert to WhatsApp link
      form.call_to_action_value = `https://wa.me/${number}`;
    }

    // Link validation for URL
    if (form.call_to_action === "link") {
      try {
        new URL(form.call_to_action_value);
      } catch {
        setError("Please enter a valid website URL.");
        return;
      }
    }

    // App link validation (optional, can check non-empty)
    if (form.call_to_action === "app" && !form.call_to_action_value.trim()) {
      setError("Please enter your App link.");
      return;
    }

    try {
      const payload = new FormData();

      if (isEditing) payload.append("ad_id", location.state.adId);

      // required
      payload.append("advertiser_id", advertiserId);
      payload.append("title", form.title);
      payload.append("description", form.description);

      // Media files
      if (form.media_file && form.media_file.length > 0) {
        form.media_file.forEach((f) => {
          payload.append("media_file", f); // must be the File object
        });
      }

      // call to action
      payload.append("call_to_action", form.call_to_action);
      payload.append("call_to_action_value", form.call_to_action_value);

      // tags, industries, categories → JSON
      const cleanArray = (arr) => arr.filter((x) => x && x.trim() !== "");

      // payload.append(
      //   "tags",
      //   JSON.stringify(cleanArray(form.tags ? form.tags.split(",") : []))
      // );

      payload.append(
        "targeting_industries",
        JSON.stringify(
          cleanArray(
            form.targeting_industries
              ? form.targeting_industries.split(",")
              : []
          )
        )
      );

      payload.append(
        "targeting_categories",
        JSON.stringify(
          cleanArray(
            form.targeting_categories
              ? form.targeting_categories.split(",")
              : []
          )
        )
      );

      // bid / budget
      payload.append("bid_price", form.bid_price);
      payload.append("max_budget", form.max_budget);

      // device_target → JSON
      payload.append(
        "device_target",
        JSON.stringify(form.device_target || "all")
      );

      // dates
      payload.append("start_date", form.start_date);
      payload.append("end_date", form.end_date);

      // zone_id (NOT zone_ids array)
      payload.append("zone_id", 1); // hardcoded for now

      // status (enum)
      payload.append("status", form.status ? "active" : "paused");

      // retargeting_override (boolean)
      payload.append("retargeting_override", form.retargeting_override ? 1 : 0);

      // ✅ debug payload
      console.log("=== FormData Payload ===");
      for (let pair of payload.entries()) {
        console.log(pair[0], pair[1]);
      }
      console.log("=======================");

      const url = isEditing
        ? `${baseUrl}/api/ads/${location.state.adId}`
        : `${baseUrl}/api/ads`;

      const method = isEditing ? "put" : "post";

      const response = await axios({
        method,
        url,
        data: payload,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("response", response);
      if (response) {
        navigate("/dashboard/Advertiser/ads");
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to save ad.");
    } finally {
      setLoading(false);
    }
  };

  if (!advertiserId)
    return (
      <Box sx={{ mt: 10, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          You must be an advertiser to create ads.
        </Typography>
      </Box>
    );

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", mt: 5, px: 2 }}>
      <Typography variant="h4" mb={3}>
        {isEditing ? "Edit Ad" : "Create New Ad"}
      </Typography>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <form onSubmit={handleSubmit} encType="multipart/form-data">
        {/* --- Ad Content --- */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 3 }}>
          <Typography variant="h6" mb={2}>
            Ad Content
          </Typography>
          <Grid container direction="column" spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Title"
                name="title"
                fullWidth
                required
                value={form.title}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                fullWidth
                multiline
                rows={4}
                required
                value={form.description}
                onChange={handleChange}
              />
            </Grid>

            {/* Media Upload */}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ mb: 1 }}
              >
                Upload Photos/Videos (max 10)
                <input
                  type="file"
                  name="media_file"
                  multiple
                  accept="image/*,video/*"
                  hidden
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setForm((prev) => ({
                      ...prev,
                      media_file: [...(prev.media_file || []), ...files], // ✅ append instead of replace
                    }));
                  }}
                />
              </Button>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {form.media_file?.map((file, idx) => (
                  <Chip
                    key={idx}
                    label={file.name}
                    onDelete={() => {
                      setForm((prev) => ({
                        ...prev,
                        media_file: prev.media_file.filter((_, i) => i !== idx), // ✅ remove by index
                      }));
                    }}
                  />
                ))}
              </Box>
            </Grid>

            {/* Zones */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Zones</InputLabel>
                <Select
                  multiple
                  value={form.zone_ids}
                  onChange={handleZoneChange}
                  renderValue={(selected) =>
                    zones
                      .filter((z) => selected.includes(z.id))
                      .map((z) => z.name)
                      .join(", ")
                  }
                >
                  {zones.map((zone) => (
                    <MenuItem key={zone.id} value={zone.id}>
                      {zone.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
        {/* --- Schedule --- */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 3 }}>
          <Typography variant="h6" mb={2}>
            Schedule
          </Typography>

          <Grid container spacing={2} alignItems="center">
            {/* Start Date */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Start Date"
                name="start_date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  min: !isEditing
                    ? new Date().toISOString().split("T")[0]
                    : undefined,
                }}
                value={form.start_date}
                onChange={handleChange}
                helperText="Start date cannot be before today"
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="End Date"
                name="end_date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  min: form.start_date
                    ? new Date(
                        new Date(form.start_date).getTime() +
                          3 * 24 * 60 * 60 * 1000
                      )
                        .toISOString()
                        .split("T")[0]
                    : undefined,
                }}
                value={form.end_date}
                onChange={handleChange}
                helperText="End date must be at least 3 days after start"
              />
            </Grid>

            {/* Status Switch */}
            <Grid item xs={12} sm={4} sx={{ textAlign: "center" }}>
              <InputLabel>Status of the Ad</InputLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.status === "active"}
                    name="status"
                    onChange={(e) => {
                      const startDate = new Date(form.start_date);
                      const endDate = new Date(form.end_date);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const duration = Math.ceil(
                        (endDate - startDate) / (1000 * 60 * 60 * 24)
                      );

                      if (!isEditing) {
                        if (
                          startDate < today ||
                          endDate <= startDate ||
                          duration < 3
                        ) {
                          setForm((prev) => ({ ...prev, status: "paused" }));
                          return;
                        }
                      }

                      setForm((prev) => ({
                        ...prev,
                        status: e.target.checked ? "active" : "paused",
                      }));
                    }}
                    color="primary"
                  />
                }
                label={form.status === "active" ? "Active" : "Paused"}
              />
              <Typography variant="caption" display="block">
                Minimum 3 days duration
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* --- Budget --- */}

        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 3 }}>
          <Typography variant="h6" mb={2}>
            Budget
          </Typography>
          <Grid container spacing={2} alignItems="center">
            {/* Bid Price */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Tooltip
                  title="Amount you pay per click, impression, or action."
                  placement="left"
                  arrow
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: 16, mr: 0.5, cursor: "pointer" }}
                  />
                </Tooltip>
                <InputLabel sx={{ mb: 0 }}>Bid Price</InputLabel>
              </Box>
              <TextField
                name="bid_price"
                type="number"
                fullWidth
                value={form.bid_price}
                onChange={handleChange}
              />
            </Grid>

            {/* Max Budget */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Tooltip
                  title="Total amount you are willing to spend for this campaign."
                  placement="left"
                  arrow
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: 16, mr: 0.5, cursor: "pointer" }}
                  />
                </Tooltip>
                <InputLabel sx={{ mb: 0, cursor: "pointer" }}>
                  Max Budget
                </InputLabel>
              </Box>
              <TextField
                name="max_budget"
                type="number"
                fullWidth
                value={form.max_budget}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* --- CTA & Targeting & Status --- */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 3 }}>
          <Typography variant="h6" mb={2}>
            Call To Action & Targeting
          </Typography>

          <Grid container spacing={3}>
            {/* CTA Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>CTA Type</InputLabel>
                <Select
                  name="call_to_action"
                  value={form.call_to_action}
                  onChange={(e) => {
                    handleChange(e);
                    // Reset CTA value when type changes
                    setForm((prev) => ({ ...prev, call_to_action_value: "" }));
                  }}
                >
                  <MenuItem value="link">Link / Website</MenuItem>
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  <MenuItem value="app">App</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* CTA Value */}
            <Grid item xs={12} sm={6}>
              <TextField
                label={
                  form.call_to_action === "whatsapp"
                    ? "WhatsApp Number (with country code)"
                    : form.call_to_action === "link"
                    ? "Website URL"
                    : "App Link"
                }
                name="call_to_action_value"
                fullWidth
                value={form.call_to_action_value}
                onChange={handleChange}
                helperText={
                  form.call_to_action === "whatsapp"
                    ? "This number will be converted to a WhatsApp link (e.g., +201234567890)"
                    : form.call_to_action === "link"
                    ? "Enter a valid website URL"
                    : "Enter your App link"
                }
                error={
                  form.call_to_action === "whatsapp" &&
                  !/^\+?[1-9]\d{7,14}$/.test(form.call_to_action_value)
                }
              />
            </Grid>

            {/* Device Target */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Device Target</InputLabel>
                <Select
                  name="device_target"
                  value={form.device_target}
                  onChange={handleChange}
                >
                  <MenuItem value="all">All Devices</MenuItem>
                  <MenuItem value="mobile">Mobile</MenuItem>
                  <MenuItem value="pc">PC</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Target Categories */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Target Categories"
                name="targeting_categories"
                fullWidth
                value={form.targeting_categories}
                onChange={handleChange}
                helperText="Separate multiple categories with commas"
              />
            </Grid>
            {/* Target Industries */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Target Industries"
                name="targeting_industries"
                fullWidth
                value={form.targeting_industries}
                onChange={handleChange}
                helperText="Separate multiple industries with commas"
              />
            </Grid>
          </Grid>
        </Paper>
        {/* --- Submit + Cancel --- */}
        <Box
          sx={{
            mb: 5,
            display: "flex",
            justifyContent: "center",
            gap: 2, // space between buttons
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ backgroundColor: "#2E4053", minWidth: 150 }}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : isEditing ? (
              "Update Ad"
            ) : (
              "Create Ad"
            )}
          </Button>

          <Button
            variant="outlined"
            color="#2E4053"
            sx={{ minWidth: 150 }}
            onClick={() => navigate("/dashboard/Advertiser/ads")}
          >
            Cancel
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default CreateAd;
